import React from 'react'
import { Avatar, Box, Divider, Flex, Text } from '@chakra-ui/core'
import styled from '@emotion/styled'
import colors from 'constants/colors'
import LocaleText from 'components/LocaleText'
import { isRTL, isValidNumberString } from 'utils'
import { RankingDiff } from 'components/Ranking/RankingSlider/RankingDiff'
import strings from 'constants/strings'

const BASE_COLORS = [
  { r: 255, g: 100, b: 170 }, // Pink
  { r: 100, g: 140, b: 240 }, // Blue
  { r: 190, g: 100, b: 210 }, // Orchid
  { r: 255, g: 130, b: 90 }, // Coral
  { r: 70, g: 210, b: 190 }, // Turquoise
  { r: 255, g: 100, b: 130 }, // Rose
  { r: 150, g: 110, b: 210 }, // Purple
  { r: 255, g: 150, b: 70 }, // Orange
  { r: 255, g: 170, b: 100 }, // Peach
  { r: 120, g: 180, b: 240 }, // Sky blue
  { r: 210, g: 120, b: 170 }, // Mauve
  { r: 160, g: 210, b: 120 }, // Lime
  { r: 255, g: 120, b: 120 }, // Red
  { r: 120, g: 160, b: 210 }, // Steel blue
  { r: 210, g: 160, b: 120 }, // Tan
] as const

const adjustSaturation = (
  color: { r: number; g: number; b: number },
  saturationPercent: number,
  alpha: number
): string => {
  // the less percentage, the closer the color to white (255, 255, 255)
  const factor = saturationPercent / 100
  const r = Math.round(color.r + (255 - color.r) * (1 - factor))
  const g = Math.round(color.g + (255 - color.g) * (1 - factor))
  const b = Math.round(color.b + (255 - color.b) * (1 - factor))

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const getBaseColorFromId = (id: string) => {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return BASE_COLORS[hash % BASE_COLORS.length]
}

// For cards with data - medium saturation (60%)
const getColorFromId = (id: string): string => {
  const baseColor = getBaseColorFromId(id)
  return adjustSaturation(baseColor, 60, 0.35)
}

// For cards without data - low saturation (30%)
const getPaleColorFromId = (id: string): string => {
  const baseColor = getBaseColorFromId(id)
  return adjustSaturation(baseColor, 30, 0.28)
}

// For shadows - high saturation (30%)
const getShadowColorFromId = (id: string): string => {
  const baseColor = getBaseColorFromId(id)
  return adjustSaturation(baseColor, 70, 0.4)
}

const StyledCard = styled(Box)<{ bgColor?: string; shadowColor?: string }>`
  cursor: pointer;
  border-radius: 12px;
  background: ${({ bgColor }) => bgColor || 'white'};
  box-shadow: ${({ shadowColor }) =>
    shadowColor
      ? `0 0px 16px ${shadowColor}, 0 3px 12px ${shadowColor}, 0 0px 4px rgba(0, 0, 0, 0.08)`
      : '0 0px 8px rgba(0, 0, 0, 0.06)'};
  transition: all 0.3s ease;
  padding: 16px;

  @media (min-width: 768px) {
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ shadowColor }) =>
      shadowColor
        ? `0 10px 32px ${shadowColor}, 0 6px 16px ${shadowColor}, 0 2px 8px rgba(0, 0, 0, 0.12)`
        : '0 4px 16px rgba(0, 0, 0, 0.12)'};
  }
`

interface RankingCardProps {
  kpiId: string
  kpiTitle: string
  managersAvatar: string
  managersFullName?: string
  storeRank: any
  isDynamicKpi: boolean
  onClick: () => void
}

export const RankingCard: React.FC<RankingCardProps> = ({
  kpiId,
  kpiTitle,
  managersAvatar,
  managersFullName,
  storeRank,
  isDynamicKpi,
  onClick,
}) => {
  const isRenderStoreProperties =
    (isDynamicKpi && storeRank && storeRank.diff !== null && storeRank.diff !== undefined) ||
    (storeRank && storeRank.target !== null && storeRank.target !== undefined) ||
    (storeRank && storeRank.performance !== null && storeRank.performance !== undefined)

  const bgColor = isRenderStoreProperties ? getColorFromId(kpiId) : getPaleColorFromId(kpiId)
  const shadowColor = getShadowColorFromId(kpiId)

  return (
    <StyledCard onClick={onClick} bgColor={bgColor} shadowColor={shadowColor} border={'2px solid white'}>
      <Flex alignItems="center" mb="16px" style={{ columnGap: '20px' }}>
        <Avatar src={managersAvatar} />
        <Flex flexDir="column" fontSize="16px" fontFamily="sans-serif" alignItems="flex-start">
          <Text
            fontSize={['16px', '16px', '18px']}
            fontWeight="600"
            color="rgba(0, 0, 0, 0.85)"
            flex="1"
            dir={isRTL(kpiTitle) ? 'rtl' : 'ltr'}
            isTruncated
            maxW="230px"
          >
            <LocaleText text={kpiTitle} />
          </Text>
          {managersFullName && (
            <Text isTruncated maxW="300px" fontSize="14px" color="rgba(0, 0, 0, 0.65)">
              {managersFullName}
            </Text>
          )}
        </Flex>
      </Flex>
      <Divider borderColor={'rgba(0, 0, 0, 0.09)'} />
      <Flex justifyContent="space-between" alignItems="center" flexDir="row-reverse" w="100%" mt="12px">
        <Box fontSize="18px" dir="ltr" color="rgba(0, 0, 0, 0.9)" fontWeight="700">
          {storeRank?.value}{' '}
          {isValidNumberString(storeRank?.value) && (
            <Text as="span" fontSize="18px">
              <LocaleText text="general_units" />
            </Text>
          )}
        </Box>
        {isRenderStoreProperties ? (
          <Flex flexGrow={1} alignItems="center" w="100%">
            <Flex flexDir="column">
              {isDynamicKpi && storeRank.diff !== null && storeRank.diff !== undefined && (
                <Flex>
                  <RankingDiff diff={storeRank.diff} />
                  {storeRank.diff !== strings.RANK_DIFF_STRING && (
                    <Text fontSize="14px" color="rgba(0, 0, 0, 0.7)">
                      <LocaleText text="ranking_diff_from_last_month" />
                    </Text>
                  )}
                </Flex>
              )}

              {storeRank.target !== null && storeRank.target !== undefined && (
                <Text fontSize="14px" color="rgba(0, 0, 0, 0.7)">
                  <LocaleText text="general_target" />
                  <Text as="span">: {storeRank.target} </Text>
                  {isValidNumberString(storeRank.target) && <LocaleText text="general_units" />}
                </Text>
              )}
            </Flex>
          </Flex>
        ) : (
          !storeRank?.value && (
            <Text fontSize="14px" color={colors.greyDark}>
              <LocaleText text="ranking_learn_more" />
            </Text>
          )
        )}
      </Flex>
    </StyledCard>
  )
}
